import type { Theme } from "@mui/system";
import type { ComicDrawerProps } from "@vapor/react-contrib-tax-compliance/ComicDrawer/ComicDrawer";
import { useMediaQuery } from "@vapor/react-material";
import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import { TaxSubmissionDetailDrawer } from "../components/tax-submission-detail-drawer/TaxSubmissionDetailDrawer";
import type { F24PreviewDrawerParams } from "./useF24PreviewDrawer";
import type { OutcomePreviewDrawerParams } from "./useOutcomePreviewDrawer";

export interface DetailDrawerParams {
    id: string;
    revoke: boolean;
}

export const useDetailDrawer = (
    params: DetailDrawerParams | undefined,
    setParams: Dispatch<SetStateAction<DetailDrawerParams | undefined>>,
    setF24PreviewParams: Dispatch<
        SetStateAction<F24PreviewDrawerParams | undefined>
    >,
    setOutcomePreviewParams: Dispatch<
        SetStateAction<OutcomePreviewDrawerParams | undefined>
    >,
): ComicDrawerProps | undefined => {
    const isXlScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.up(theme.breakpoints.values.xl),
    );

    return useMemo(() => {
        if (!params) return undefined;

        return {
            content: (
                <TaxSubmissionDetailDrawer
                    onClickF24Preview={(
                        taxSubmissionId,
                        taxPayerId,
                        taxPayerName,
                        taxPayerPosition,
                        delegationPosition,
                    ) => {
                        setF24PreviewParams({
                            taxSubmissionId,
                            taxPayerId,
                            taxPayerName,
                            taxPayerPosition,
                            delegationPosition,
                        });
                    }}
                    onClickPreviewOutcome={(
                        availablesOutcomeTypes,
                        governmentAgencyType,
                        taxSubmissionId,
                        selectedOutcomeType,
                        taxPayer,
                    ) => {
                        setOutcomePreviewParams({
                            availableOutcomeTypes: availablesOutcomeTypes,
                            governmentAgencyType,
                            taxSubmissionId,
                            selectedOutcomeType,
                            taxPayer,
                        });
                    }}
                    onUpdateData={() => setParams(undefined)}
                    revokeTaxPayers={params.revoke}
                    taxSubmissionId={params.id}
                />
            ),
            onClickCloseButton: () => setParams(undefined),
            open: true,
            width: isXlScreen ? "45%" : "65%",
        };
    }, [
        params,
        setParams,
        setF24PreviewParams,
        setOutcomePreviewParams,
        isXlScreen,
    ]);
};
